import React from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
  ControllerRenderProps,
  useFormState,
} from 'react-hook-form';
import {
  FormInputText,
  FormInputNumber,
  FormInputCurrency,
  FormInputDate,
  FormSelect,
  FormInputBoolean,
  TextArea,
  FormInputTextProps,
  FormInputNumberProps,
  FormInputCurrencyProps,
  FormInputDateProps,
  FormSelectProps,
  FormInputBooleanProps,
  TextAreaProps,
  FormField,
  FormMultiSelect,
  FormMultiSelectProps,
} from '@fashionnovateam/nova-ui-toolkit';

type BaseControlledFormInputProps<T = never> = {
  name: string;
  control: Control<T extends FieldValues ? T : never>;
  label: string | undefined;
  required?: boolean;
  disabled?: boolean;
  rules?: Omit<
    RegisterOptions<T extends FieldValues ? T : never>,
    'valueAsDate'
  >;
};

export interface ControlledFormInputProps<T = never>
  extends BaseControlledFormInputProps<T> {
  inputType:
    | 'text'
    | 'number'
    | 'currency'
    | 'date'
    | 'select'
    | 'textarea'
    | 'boolean'
    | 'multiselect';
  inputProps?:
    | Omit<FormInputTextProps<string>, 'label'>
    | Omit<FormInputNumberProps<string>, 'label'>
    | Omit<FormInputCurrencyProps<string>, 'label'>
    | Omit<FormInputDateProps, 'label'>
    | Omit<FormSelectProps<string, string>, 'label'>
    | Omit<TextAreaProps, 'label'>
    | Omit<FormInputBooleanProps<string>, 'label'>
    | Omit<FormMultiSelectProps<string, string>, 'label'>;
}

export function ControlledFormInput<T = never>({
  name,
  label,
  control,
  inputType,
  required,
  rules,
  disabled,
  inputProps = {},
}: ControlledFormInputProps<T>) {
  const fieldId = React.useId();
  const { isSubmitted } = useFormState({ control });
  const isRequired = required && !disabled;

  const renderInput = (
    field: ControllerRenderProps<T extends FieldValues ? T : never>,
    error?: string,
  ) => {
    const commonProps = {
      label,
      name,
      value: field.value === 0 ? '0' : (field.value ?? ''),
      onChangeValue: (val: number) => {
        if (val === undefined) {
          field.onChange('');
        } else {
          field.onChange(val);
        }
      },
      error,
      disabled,
      required: isRequired,
      ...inputProps,
    };

    switch (inputType) {
      case 'number':
        return (
          <FormInputNumber
            {...(commonProps as FormInputNumberProps<string>)}
            // Overwriting default onChangeValue to keep the null value
            // This is useful when a user just wants to reset the number value on optional fields
            onChangeValue={(val: number | undefined) => {
              field.onChange(Number.isFinite(val) ? Number(val) : null);
            }}
          />
        );
      case 'currency':
        return (
          <FormInputCurrency
            {...(commonProps as FormInputCurrencyProps<string>)}
            // Overwriting default onChangeValue to keep the null value
            // This is useful when a user just wants to reset the number value on optional fields
            onChangeValue={(val: number | undefined) => {
              field.onChange(Number.isFinite(val) ? Number(val) : null);
            }}
          />
        );
      case 'date':
        return <FormInputDate {...(commonProps as FormInputDateProps)} />;
      case 'select':
        return (
          <FormSelect {...(commonProps as FormSelectProps<string, string>)} />
        );
      case 'multiselect':
        return (
          <FormMultiSelect
            {...(commonProps as FormMultiSelectProps<string, string>)}
            value={
              // eslint-disable-next-line no-nested-ternary
              Array.isArray(field.value)
                ? field.value
                : field.value
                  ? [field.value]
                  : []
            }
            onChange={(values) => {
              if (
                (
                  inputProps as Omit<
                    FormMultiSelectProps<string, string>,
                    'label'
                  >
                ).singleSelectWhenSearchable
              ) {
                field.onChange(values?.[0] || null);
              } else {
                field.onChange(values);
              }
            }}
          />
        );
      case 'textarea': {
        const { onChangeValue, ...rest } = commonProps;
        return (
          <FormField
            required={isRequired}
            label={label || ''}
            inputId={fieldId}
            error={error}
            name={name}
          >
            <TextArea
              id={name}
              {...(rest as TextAreaProps)}
              onChange={(e) => {
                const val = e.target.value;
                if (val === undefined) {
                  field.onChange('');
                } else {
                  field.onChange(val);
                }
              }}
            />
          </FormField>
        );
      }
      case 'boolean': {
        const { onChangeValue, ...rest } = commonProps;
        return (
          <FormInputBoolean
            {...(rest as FormInputBooleanProps<string>)}
            onChangeValue={(val: boolean) => {
              if (val === undefined) {
                field.onChange(false);
              } else {
                field.onChange(val);
              }
            }}
          />
        );
      }
      case 'text':
      default:
        return (
          <FormInputText {...(commonProps as FormInputTextProps<string>)} />
        );
    }
  };

  return (
    <Controller
      control={control}
      name={name as Path<T extends FieldValues ? T : never>}
      rules={{ ...rules, required: isRequired }}
      render={({ field, fieldState: { error, isTouched, isDirty } }) => {
        let errorMsg: string | undefined;
        if ((isSubmitted || isTouched || isDirty) && error) {
          errorMsg =
            error.type === 'required'
              ? 'This field is required'
              : error.message;
        }
        return renderInput(field, errorMsg);
      }}
    />
  );
}
